import axiosInstance from "config/axios";
import { FileObject } from "mui-file-dropzone";
import Papa, { ParseError, ParseResult } from "papaparse";
import { Store } from "pullstate";
import {
  CreateFileJobRequest,
  FileColumnMapping,
  FileDataRequest,
  ImportService,
  ResponseCode,
} from "services/organizationPortalClient";

const FILE_CHUNK_SIZE = 3000; // size in bytes

type ImportsUploadStore = {
  data: unknown[] | null;
  columns?: string[];
  errors: ParseError[];
  file: File | undefined;
  fileObject: FileObject | undefined;
  jobId: string | undefined;
  totalRows: number | undefined;
};

type DataImportBulkUploadStore = {
  bulkUploadProgressStatus?: number;
  showBulkUploadProgress: boolean;
  showSuccessSnackBar: boolean;
  shouldAbortUpload: boolean;
  cancelledSnackbarMessageId?: string;
  showCancelledSnackbar: boolean;
  showErrorSnackbar: boolean;
  uploadCompleted: boolean;
  errorSnackbarMessage: { field: string; value: string };
};

const importService = new ImportService("", axiosInstance);

const matchesColumns = (actual: string[], expected: string[]): boolean =>
  actual.length === expected.length &&
  expected.every(
    (name, index) => actual[index].toLowerCase() === name.toLowerCase()
  );

const importsUploadStore = new Store<ImportsUploadStore>({
  data: null,
  columns: [],
  errors: [],
  file: undefined,
  jobId: undefined,
  fileObject: undefined,
  totalRows: undefined,
});

export const importsBulkUploadStore = new Store<DataImportBulkUploadStore>({
  showBulkUploadProgress: false,
  showSuccessSnackBar: false,
  shouldAbortUpload: false,
  showCancelledSnackbar: false,
  showErrorSnackbar: false,
  uploadCompleted: false,
  errorSnackbarMessage: { field: "", value: "" },
});

/**
 * Parses and validates the chosen file.
 *
 * `expectedColumns` is checked against the CSV header. Rows are later sliced by column index rather
 * than by name, so a file whose header does not match silently shifts every value into the wrong
 * field — harmless when adding users, destructive when deactivating them.
 */
export const importFile = async (
  file: FileObject,
  expectedColumns?: string[]
): Promise<[boolean, string?]> => {
  let fileRef = file.file;

  return new Promise((resolve) => {
    Papa.parse(fileRef, {
      complete: async (parseResult: ParseResult<[boolean, string?]>) => {
        let errors: ParseError[] = [];
        let totalRows = 0;
        errors = parseResult.errors;
        let data = parseResult.data.filter((x: any) =>
          Object.values(x).some((value: any) => value.trim() !== "")
        );
        totalRows = data.length;

        const columns = parseResult.meta.fields?.map((x) => x.trim()) ?? [];

        let result: [boolean, string?] = [true, undefined];

        if (totalRows === 0) {
          result = [false, "fileEmptyOrNoRows"];
        }

        if (
          result[0] &&
          expectedColumns &&
          !matchesColumns(columns, expectedColumns)
        ) {
          result = [false, "fileMustHaveExactlyThreeColumns"];
        }

        if (errors.length > 0 && totalRows > 0) {
          result = [
            false,
            errors.map((x) => `Row N°: ${x.row! + 1} - ${x.message}`).join(","),
          ];
        }

        importsUploadStore.update((s) => {
          s.data = data;
          s.columns = columns;
          s.errors = parseResult.errors;
          s.file = file.file;
          s.fileObject = file;
          s.totalRows = totalRows - 1;
        });

        resolve(result);
      },
      header: true,
      skipEmptyLines: true,
    });
  });
};

export const processImports = async (
  file: File,
  importJobTypeId: string,
  columnMappings: FileColumnMapping[],
  postMapping?: (data: any[][]) => any[][]
): Promise<void> => {
  importsBulkUploadStore.update((s) => {
    s.bulkUploadProgressStatus = 0;
    s.showBulkUploadProgress = true;
    s.showCancelledSnackbar = false;
    s.showErrorSnackbar = false;
    s.showSuccessSnackBar = false;
    s.shouldAbortUpload = false;
    s.uploadCompleted = false;
  });

  const progressStatusChunkSize = Math.min(
    (FILE_CHUNK_SIZE * 100) / file.size,
    100
  );

  let importJobId: string = "";
  let rowIndex = 1;

  try {
    let migrationJobResponse = await importService.createImportJob(
      new CreateFileJobRequest({
        id: undefined,
        importJobTypeId,
        fileName: file.name,
      })
    );

    if (migrationJobResponse.responseCode === ResponseCode.OK) {
      importJobId = migrationJobResponse.id!;
      importsUploadStore.update((s) => {
        s.jobId = importJobId;
      });
    }
  } catch (error) {
    importsBulkUploadStore.update((s) => {
      s.showCancelledSnackbar = true;
    });
    return;
  }

  Papa.parse(file, {
    download: true,
    skipEmptyLines: true,
    fastMode: false,
    quoteChar: '"',
    chunk: async function (chunk, parser) {
      let chunkData = chunk.data;
      if (rowIndex === 1) {
        chunk.data.shift();
      }

      if (importsBulkUploadStore.getRawState().shouldAbortUpload) {
        importsBulkUploadStore.update((s) => {
          s.showCancelledSnackbar = true;
        });
        parser.abort();
        return;
      }

      parser.pause();

      if (chunk.errors.length) {
        importsBulkUploadStore.update((s) => {
          s.shouldAbortUpload = true;
          s.showCancelledSnackbar = true;
          s.cancelledSnackbarMessageId = "bulkUploadFileContentError";
        });
        parser.abort();
        return;
      }

      let data = getNeededData(chunkData, columnMappings);

      if (postMapping) {
        data = postMapping(data);
      }

      const importDataRequest = new FileDataRequest({
        data,
        columnMappings,
        rowIndex,
      });

      rowIndex += data.length;

      try {
        await importService.importFileData(importJobId, importDataRequest);

        importsBulkUploadStore.update((s) => {
          s.bulkUploadProgressStatus =
            s.bulkUploadProgressStatus! + progressStatusChunkSize;
        });
        parser.resume();
      } catch (err) {
        console.error(err);

        importsBulkUploadStore.update((s) => {
          s.shouldAbortUpload = true;
          s.showCancelledSnackbar = true;
          s.cancelledSnackbarMessageId = "errorPleaseTryAgain";
        });
        parser.abort();
        return true;
      }
    },
    chunkSize: FILE_CHUNK_SIZE,
    complete: function () {
      importsBulkUploadStore.update((s) => {
        s.showBulkUploadProgress = false;
      });
      if (!importsBulkUploadStore.getRawState().shouldAbortUpload) {
        importsBulkUploadStore.update((s) => {
          s.showSuccessSnackBar = true;
          s.uploadCompleted = true;
        });
      }
    },
  });
};

const getNeededData = (
  rawData: any[],
  columnMappings: FileColumnMapping[]
): any[][] => {
  const neededData: any[][] = [];

  /*Clear rows which contains only empty values*/
  const sanitized = rawData.filter((x: any) =>
    Object.values(x).some((value: any) => value.trim() !== "")
  );

  sanitized.forEach((element, index) => {
    columnMappings.forEach((mapping) => {
      if (neededData[index] === undefined) neededData[index] = [];

      neededData[index].push(element[mapping.columnIndex]);
    });
  });

  return neededData;
};

export const closeBulkImportsSuccessSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showSuccessSnackBar = false;
  });
};

export const closeBulkImportsCancelledSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showCancelledSnackbar = false;
  });
};

export const closeBulkImportsErrorSnackbar = () => {
  importsBulkUploadStore.update((s) => {
    s.showErrorSnackbar = false;
  });
};

export const abortBulkImportsUpload = () => {
  importsBulkUploadStore.update((s) => {
    s.shouldAbortUpload = true;
  });
};

export default importsUploadStore;
